import React, { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Box } from '@material-ui/core';
import SyncIcon from '@material-ui/icons/Sync';
import { wait } from 'globals/utils';
import defaultLocales from 'globals/locales/default';
import FsPendingButton from 'ui-elements/buttons/FsPendingButton';
import { isWeekPasswordRe } from 'ui-elements/form-elements/FsPasswordStrengthChecker/PasswordStrengthChecker.utils';
import PasswordUpdateSingleRow from './PasswordUpdateSingleRow';
import { PasswordUpdateSingleRowDataProps } from './PasswordUpdateSingleRow/PasswordUpdateSingleRow';

interface AdditionalIdx {
  id: number;
}

interface FsPasswordUpdateFormProps {
  rows: PasswordUpdateSingleRowDataProps[];
  onChange: () => void;
}

const FsPasswordUpdateForm: React.FC<FsPasswordUpdateFormProps> = ({
  rows,
  onChange,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [pending, setPending] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(false);
  const [originRows, setOriginRows] = useState<
    Array<PasswordUpdateSingleRowDataProps & AdditionalIdx>
  >(
    rows.map((row, idx) => ({
      id: idx,
      ...row,
    }))
  );

  const handleChange = (
    changedField: PasswordUpdateSingleRowDataProps & AdditionalIdx
  ) => {
    setOriginRows((currentRows) =>
      currentRows.map((field) => {
        return field.id === changedField.id ? changedField : field;
      })
    );
  };

  const handleUpdate = async () => {
    setPending(true);
    const payload = originRows
      .map(({ name, value }) => ({
        name,
        value,
      }))
      .reduce((obj, item) => ({ ...obj, [item.name]: item.value }), {});

    // eslint-disable-next-line no-console
    console.log('UPDATE:', payload);
    await wait(1000);
    setPending(false);
    enqueueSnackbar(defaultLocales.passwordUpdateNotificationSuccess, {
      variant: 'success',
    });
    onChange();
  };

  const getFieldValue = useCallback(
    (key: string) => {
      return originRows?.find(({ name }) => name === key)?.value;
    },
    [originRows]
  );

  useEffect(() => {
    const currentPasswordValue = getFieldValue('currentPassword');
    const newPasswordValue = getFieldValue('newPassword');
    const repeatNewPasswordValue = getFieldValue('repeatNewPassword');

    const isCurrentPasswordValid =
      !!currentPasswordValue && currentPasswordValue.length >= 3;
    const isNewPasswordValid =
      !!newPasswordValue && !!newPasswordValue.match(isWeekPasswordRe);
    const isRepeatNewPasswordValid =
      newPasswordValue === repeatNewPasswordValue;

    setIsValid(
      isCurrentPasswordValid && isNewPasswordValid && isRepeatNewPasswordValid
    );
  }, [getFieldValue, originRows]);

  return (
    <Box>
      <Box>
        {/* <pre>{JSON.stringify(originRows, null, 2)}</pre> */}
        {originRows.map(({ id, ...otherProps }) => (
          <PasswordUpdateSingleRow
            key={id}
            {...otherProps}
            onChange={(changedField) => handleChange({ id, ...changedField })}
          />
        ))}
      </Box>
      <FsPendingButton
        pending={pending}
        onClick={handleUpdate}
        startIcon={<SyncIcon />}
        disabled={!isValid}
      >
        {defaultLocales.update}
      </FsPendingButton>
    </Box>
  );
};

export default FsPasswordUpdateForm;
